import React from "react";
import { useDispatch, useSelector } from "react-redux";
// import { selectModuleSorting, setModuleSorting, resetModuleSorting } from "../../../store/slices/filterSorting.slice";
import {
  useFloating,
  offset,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  flip,
  shift,
} from "@floating-ui/react";  
 
import { TbArrowsSort } from "react-icons/tb";
import DefaultButton from "../buttons/defaultButton";
import { selectModuleSorting, setModuleSorting, resetModuleSorting } from "../../../store/slices/filterSorting.slice";
 

const AdvanceSorting = ({
  module, // required: module name for per-module sorting
  setIsSorting, // optional: for legacy compatibility
  isSorting: isSortingProp, // optional: for legacy compatibility
  onSortingChange = () => null,
  isFilterDisable = true,
  isDateSorting = true,
  disableText = "",
  isOtherSortText = "",
  sortByDateText = "",
  sortByNameText = "",
  sortingButtonText = { ascending: "A-Z", descending: "Z-A" },
  dateSortingButtonText = null,
  nameSortingButtonText = null,
  otherSortingButtonText = null,
}) => {
  const dispatch = useDispatch();
  const sorting = useSelector((state) => selectModuleSorting(state, module));
  const [open, setOpen] = React.useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    placement: "bottom-start",
    strategy: "fixed",
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  // 1. Memoize updateSorting, applySorting, clearSorting
  const updateSorting = React.useCallback((changes) => {
    // When updating sorting options, also set isSorting to true if any sort option is active
    const hasActiveSort = changes.isSortDate || changes.isSortName || changes.isOtherSort;
    const updatedChanges = {
      ...changes,
      // Set isSorting to true if we're enabling a sort option, or keep existing value
      isSorting: hasActiveSort ? true : (sorting.isSorting || false)
    };
    
    // Debug logging
    
    dispatch(setModuleSorting({ module, sortingState: updatedChanges }));
  }, [dispatch, module, sorting.isSorting]);

  const applySorting = React.useCallback(() => {
    onSortingChange(sorting);
    updateSorting({ isSorting: true });
    if (setIsSorting) setIsSorting(true);
    setOpen(false);
  }, [onSortingChange, sorting, updateSorting, setIsSorting]);

  const clearSorting = React.useCallback(() => {
    dispatch(resetModuleSorting(module));
    onSortingChange({});
    if (setIsSorting) setIsSorting(false);
    setOpen(false);
  }, [dispatch, module, onSortingChange, setIsSorting]);
  
 
  
  return (
    <div className="relative inline-block">
      <DefaultButton
        ref={refs.setReference}
        {...getReferenceProps()}
        // variant={sorting.isSorting ? "danger" : "primary"}
        classnames={sorting.isSorting ? "bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white gap-1" : "bg-white gap-1"}
        tooltip="Sort"
        icon={<TbArrowsSort className="w-4 h-4" />}
      />
      {open && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          className="z-[9999] w-72 rounded-2xl border border-gray-200 bg-white p-5 shadow-xl space-y-4"
        >
          <div className="flex items-center justify-between">
            <p className="text-md font-semibold  text-primary-950 mb-2">
              Sorting
            </p>
            <p
              onClick={clearSorting}
              className="cursor-pointer text-cs flex items-center gap-1 text-primary-600 hover:text-primary-950 hover:underline"
            >
              Clear Sorting
            </p>
          </div>
          {/* Sort by Date */}
          {isDateSorting && (
          <div>
            <div className="flex items-center justify-between mb-2 ">
              <p className="text-sm font-semibold text-gray-700">
                {`Sort by ${sortByDateText ? sortByDateText : "Date"}`}
              </p>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                    checked={sorting.isSortDate}
                  onChange={(e) => {
                      updateSorting({
                        isSortDate: e.target.checked,
                        isSortName: false,
                        isOtherSort: false,
                      });
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-primary-600 transition-colors"></div>
                <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow-md transition-transform peer-checked:translate-x-5"></div>
              </label>
            </div>
              {sorting.isSortDate && (
              <div className="flex gap-3">
                <button
                    onClick={() => updateSorting({ sortDate: "asc" })}
                  className={`w-full py-1.5 text-sm font-medium rounded-md border transition-colors ${
                      sorting.sortDate === "asc"
                      ? "bg-primary-600 text-white border-primary-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {(dateSortingButtonText || sortingButtonText).ascending}
                </button>
                <button
                    onClick={() => updateSorting({ sortDate: "desc" })}
                  className={`w-full py-1.5 text-sm font-medium rounded-md border transition-colors ${
                      sorting.sortDate === "desc"
                      ? "bg-primary-600 text-white border-primary-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {(dateSortingButtonText || sortingButtonText).descending}
                </button>
              </div>
            )}
          </div>
          )}
          {/* Sort by Name */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-700">
                Sort by {sortByNameText ? sortByNameText : "Name"}
              </p>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={sorting.isSortName}
                  onChange={(e) => {
                    updateSorting({
                      isSortName: e.target.checked,
                      isSortDate: false,
                      isOtherSort: false,
                    });
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-primary-600 transition-colors"></div>
                <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow-md transition-transform peer-checked:translate-x-5"></div>
              </label>
            </div>
            {sorting.isSortName && (
              <div className="flex gap-3">
                <button
                  onClick={() => updateSorting({ sortName: "asc" })}
                  className={`w-full py-1.5 text-sm font-medium rounded-md border transition-colors ${
                    sorting.sortName === "asc"
                      ? "bg-primary-600 text-white border-primary-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {(nameSortingButtonText || sortingButtonText).ascending}
                </button>
                <button
                  onClick={() => updateSorting({ sortName: "desc" })}
                  className={`w-full py-1.5 text-sm font-medium rounded-md border transition-colors ${
                    sorting.sortName === "desc"
                      ? "bg-primary-600 text-white border-primary-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {(nameSortingButtonText || sortingButtonText).descending}
                </button>
              </div>
            )}
            
            {/* Debug info for Name sorting
            {sorting.isSortName && (
              <div className="text-xs text-gray-400 mt-1">
                Name sort debug: sortName = {sorting.sortName || 'undefined'}
              </div>
            )} */}
          </div>
          {isOtherSortText && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-gray-700">
                  Sort by {isOtherSortText}
                </p>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sorting.isOtherSort}
                    onChange={(e) => {
                      updateSorting({
                        isOtherSort: e.target.checked,
                        isSortDate: false,
                        isSortName: false,
                      });
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-primary-600 transition-colors"></div>
                  <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow-md transition-transform peer-checked:translate-x-5"></div>
                </label>
              </div>
              {sorting.isOtherSort && (
                <div className="flex gap-3">
                  <button
                    onClick={() => updateSorting({ otherSort: "asc" })}
                    className={`w-full py-1.5 text-sm font-medium rounded-md border transition-colors ${
                      sorting.otherSort === "asc"
                        ? "bg-primary-600 text-white border-primary-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {(otherSortingButtonText || sortingButtonText).ascending}
                  </button>
                  <button
                    onClick={() => updateSorting({ otherSort: "desc" })}
                    className={`w-full py-1.5 text-sm font-medium rounded-md border transition-colors ${
                      sorting.otherSort === "desc"
                        ? "bg-primary-600 text-white border-primary-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {(otherSortingButtonText || sortingButtonText).descending}
                  </button>
                </div>
              )}
            </div>
          )}
          {/* Show Disabled */}
          {isFilterDisable && (
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-700">
                {`Show ${disableText ? disableText : "Disabled"}`}
              </p>
              {/* Toggle (placeholder switch style) */}
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={sorting.showDisabled}
                  onChange={(e) => updateSorting({ showDisabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-primary-600 transition-colors"></div>
                <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow-md transition-transform peer-checked:translate-x-5"></div>
              </label>
            </div>
          )}
          {/* Apply Button */}
          <DefaultButton
            classnames="w-full "
            variant="default"
            name="Apply Sorting"
            onClick={applySorting}
            disabled={
              !sorting.isSortDate && !sorting.isSortName && !sorting.showDisabled && !sorting.isOtherSort
            }
          ></DefaultButton>
          
          {/* Debug info */}
          {/* <div className="text-xs text-gray-500 mt-2">
            Debug: isSortDate: {sorting.isSortDate ? 'true' : 'false'}, 
            isSortName: {sorting.isSortName ? 'true' : 'false'}, 
            isSorting: {sorting.isSorting ? 'true' : 'false'}
          </div> */}
        </div>
      )}
    </div>
  );
};
export default AdvanceSorting;
